import { useRef, useCallback, useState } from 'react'
import { splitPdfPageImages } from '@utils/pdfs'
import { blobToDataURI, appendFileName } from '@utils/files'
import dayjs from 'dayjs'
import { sleep } from '@utils'

const initialLog = {
    timestamp: dayjs().format('HH:mm A'),
    message: 'Ready',
}

const pageGridColsMap = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
}

const KSClientPdfSplitViewModel = () => {
    
    const fileInputRef = useRef()
    
    const [loading, setLoading] = useState(false)
    const [file, setFile] = useState(null)
    const [logs, setLogs] = useState([initialLog])
    const [blobUris, setBlobUris] = useState(null)
    const [modalImage, setModalImage] = useState(null)
    const [splitProgressMax, setSplitProgressMax] = useState(0)
    const [splitProgressValue, setSplitProgressValue] = useState(0)
    
    const addLog = message => setLogs(prev => [
        { timestamp: dayjs().format('HH:mm A'), message },
        ...prev,
    ])
    
    const reset = () => {
        
        setLoading(true)
        setBlobUris(null)
        setSplitProgressMax(0)
        setSplitProgressValue(0)
        
    }
    
    const clearFile = useCallback(() => {
        
        setFile(null)
        
        if (fileInputRef.current)
            fileInputRef.current.value = null
        
    }, [fileInputRef])
    
    /**
     * @param {File} file - The original PDF file to use for naming the pages
     * @param {SplitPdfPageImagesResultPayload[]} payloads
     * @returns {Promise<SplitPdfPageImagesResultPayloadWithDataUri[]>}
     */
    const splitPdfPageImagesResultPayloadsToDataURIs = async (file, payloads) => {
        
        const promises = payloads.map(async payload => ({
            blob: await blobToDataURI(payload.blob),
            fileName: appendFileName(file.name, null, `-${payload.fileName}`, false),
        }))
        
        const results = await Promise.all(promises)
        
        return results.sort((a, b) => a.pageNumber - b.pageNumber)
        
    }
    
    const createPdfPages = async file => {
        
        try {
            
            /** @type {SplitPdfPageImagesResult} */
            const { results, stats } = await splitPdfPageImages(
                file,
                value => setSplitProgressValue(value),
                totalPages => {
                    addLog(`Creating ${totalPages} PDF pages`)
                    setSplitProgressMax(totalPages)
                },
                () => {
                    setSplitProgressMax(0)
                    setSplitProgressValue(0)
                },
            )
            
            const resultsWithDataUris = await splitPdfPageImagesResultPayloadsToDataURIs(file, results)
            
            setSplitProgressValue(0)
            
            addLog(`[stats] numPages: ${stats.numPages}`)
            addLog(`[stats] avgTimePerPage: ${stats.avgTimePerPageSeconds.toFixed(2)} seconds`)
            addLog(`[stats] total duration: ${stats.totalDurationSeconds.toFixed(2)} seconds`)
            
            return resultsWithDataUris
            
        } catch (e) {
            
            console.error('createPdfPages', { file }, e)
            return null
            
        }
        
    }
    
    const renderSplitFile = useCallback(async nextFile => {
        
        reset()
        
        try {
            
            const sourceFile = nextFile || file
            const results = await createPdfPages(sourceFile)
            
            addLog('Rendering PDF pages')
            await sleep(100)
            
            setBlobUris(results)
            
            addLog('Done creating PDF pages')
            
        } catch (e) {
            
            console.error('renderSplitFile', e)
            addLog(`Error: ${e.message}`)
            reset()
            
        }
        
        setLoading(false)
        
    }, [file])
    
    const handleFileChange = useCallback(async e => {
        
        if (logs.length)
            addLog('----')
        
        reset()
        const nextFile = e.target.files[0]
        
        setFile(nextFile)
        addLog(`Uploading file ${nextFile.name}`)
        
        await renderSplitFile(nextFile)
        
    }, [logs])
    
    return {
        
        // Constants
        pageGridColsMap,
        
        // Refs
        fileInputRef,
        
        // State
        loading,
        setLoading,
        file,
        setFile,
        logs,
        setLogs,
        blobUris,
        setBlobUris,
        modalImage,
        setModalImage,
        splitProgressMax,
        setSplitProgressMax,
        splitProgressValue,
        setSplitProgressValue,
        
        // Actions
        addLog,
        clearFile,
        handleFileChange,
        renderSplitFile,
        
    }
    
}

export default KSClientPdfSplitViewModel
